import type { CustomerFlag, CustomerRecord, Waiver } from '@/domain/types';

/**
 * Derive customer-level flags for display. Pure — no store access.
 *
 * Medical flags (`allergy` / `medical-condition` / `under-medication`) are
 * unioned from two sources: the answers on the customer's latest *signed*
 * waiver, and the customer's standing `medicalProfile`. `waiver-signed` is
 * raised only when a signed waiver is passed in.
 *
 * @param customer       the customer to flag
 * @param signedWaiver   the customer's latest signed waiver, if any (a non-signed
 *                       waiver is ignored — its answers aren't trusted yet)
 */
export function deriveCustomerFlags(
  customer: CustomerRecord,
  signedWaiver?: Waiver,
): CustomerFlag[] {
  const flags: CustomerFlag[] = [];

  const answers = signedWaiver?.status === 'signed' ? signedWaiver.answers : undefined;
  const med = customer.medicalProfile;

  if (signedWaiver?.status === 'signed') flags.push('waiver-signed');

  const hasCondition = nonEmpty(answers?.conditions) || nonEmpty(med?.conditions);
  const hasAllergy = nonEmpty(answers?.allergies) || nonEmpty(med?.allergies);
  const hasMedication = nonEmpty(answers?.medications) || nonEmpty(med?.medications);

  if (hasCondition) flags.push('medical-condition');
  if (hasAllergy) flags.push('allergy');
  if (hasMedication) flags.push('under-medication');

  // Photo release is opt-out — an explicit `false` means "do not photograph me".
  if (answers?.photoConsent === false) flags.push('photo-opt-out');

  return flags;
}

function nonEmpty(list?: string[]): boolean {
  return (list?.length ?? 0) > 0;
}

/** Pick the most recently signed waiver from a list (by `signedAt`). */
export function pickLatestSignedWaiver(waivers: Waiver[]): Waiver | undefined {
  return waivers
    .filter((w) => w.status === 'signed')
    .sort((a, b) => (b.signedAt ?? '').localeCompare(a.signedAt ?? ''))[0];
}
